import { Breakpoint } from "@mui/material/styles";
import { IJumpMarkItem, ISectionVisibility } from "src/components-mui/common/jump-marks/jump-marks.types";
import { TResponsivePx } from "src/types/responsive";
import { sumResponsivePx } from "src/utils/mui/responsive-px/responsive-px";

interface IJumpMarkTarget {
    items: readonly IJumpMarkItem[];
    id: string;
}

export const isPageTopItem = ({ items, id }: IJumpMarkTarget) => items.some(item => item.id === id && item.isPageTop === true);

export const getJumpMarkHref = (target: IJumpMarkTarget) => (isPageTopItem(target) ? "#" : `#${target.id}`);

export const getJumpMarkUrl = ({
    location: { pathname, search },
    ...target
}: IJumpMarkTarget & { location: Pick<Location, "pathname" | "search"> }) => (isPageTopItem(target) ? `${pathname}${search}` : `#${target.id}`);

const GAP_BELOW_STICKY_HEADERS_PX: TResponsivePx = { xs: 0, sm: 8 };

interface ISectionScrollMarginParams {
    stickyNavHeightPx: TResponsivePx;
    barHeightPx: TResponsivePx;
    breakpoints: readonly Breakpoint[];
}

export const getSectionScrollMarginPx = ({ stickyNavHeightPx, barHeightPx, breakpoints }: ISectionScrollMarginParams) =>
    sumResponsivePx({ summandsPx: [stickyNavHeightPx, barHeightPx, GAP_BELOW_STICKY_HEADERS_PX], breakpoints });

export const getObserverRootMargin = (scrollMarginPx: number) => `-${scrollMarginPx}px 0px 0px 0px`;

interface IFindActiveJumpMarkParams {
    items: readonly IJumpMarkItem[];
    sectionIds: ReadonlyMap<Element, string>;
    visibleSections: ReadonlyMap<Element, ISectionVisibility>;
}

export const findActiveJumpMarkId = ({ items, sectionIds, visibleSections }: IFindActiveJumpMarkParams) => {
    const lastItemId = items.at(-1)?.id;
    const visibleIds = new Set<string>();
    let isLastItemFullyVisible = false;

    visibleSections.forEach(({ isFullyVisible, hasHeight }, element) => {
        const id = sectionIds.get(element);

        if (id === undefined) {
            return;
        }

        visibleIds.add(id);
        isLastItemFullyVisible ||= id === lastItemId && isFullyVisible && hasHeight;
    });

    if (isLastItemFullyVisible) {
        return lastItemId;
    }

    return items.find(item => visibleIds.has(item.id))?.id;
};

interface INearestScrollLeftParams {
    itemStart: number;
    itemEnd: number;
    scrollLeft: number;
    viewportWidth: number;
}

export const getNearestScrollLeft = ({ itemStart, itemEnd, scrollLeft, viewportWidth }: INearestScrollLeftParams) => {
    if (itemStart < scrollLeft) {
        return itemStart;
    }

    if (itemEnd > scrollLeft + viewportWidth) {
        return itemEnd - viewportWidth;
    }

    return scrollLeft;
};
